// NEW_FILE_JUSTIFIED: AI Reply trigger button for chat header — standalone so ChatPanel stays under 200 lines

import { useState, useCallback } from 'react';
import { Sparkles } from 'lucide-react';
import { useAction } from 'convex/react';
import { api } from '@convex/_generated/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { IAiReplyButtonProps } from '@/@types/aiReply';

export function AiReplyButton({ contactIdentifier, platform, contactName }: IAiReplyButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const triggerManual = useAction(api['aiReply/pipelineActions'].triggerManual);

  const handleClick = useCallback(async () => {
    setIsLoading(true);
    try {
      await triggerManual({ contactIdentifier, platform, contactName });
      toast.success('AI draft created');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate AI reply';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [triggerManual, contactIdentifier, platform, contactName]);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      aria-label="Generate AI reply draft"
      className={cn(
        'flex h-9 items-center gap-1.5 rounded-lg px-3',
        'bg-muted text-muted-foreground text-xs font-medium',
        'hover:bg-primary hover:text-primary-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        'transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
      )}
    >
      <Sparkles size={14} aria-hidden="true" className={cn(isLoading && 'animate-pulse')} />
      {isLoading ? 'Generating…' : 'AI Reply'}
    </button>
  );
}
